import * as THREE from "three";

/**
 * A flat, pixelated white RING laid on the ground under the currently-selected target (player / NPC /
 * mob). One reused mesh that the world controller moves to follow the selection each frame and hides
 * when nothing is selected. NearestFilter on a small canvas gives the chunky pixel-art look.
 */

const DIAM = 1.5; // ring diameter in world units

/** Draw a crisp white annulus on a small canvas → a pixelated ring once nearest-sampled + scaled up. */
function makeRingTexture(): THREE.CanvasTexture {
  const S = 32, c = document.createElement("canvas"); c.width = c.height = S;
  const ctx = c.getContext("2d")!;
  const img = ctx.createImageData(S, S);
  const cx = (S - 1) / 2, cy = (S - 1) / 2;
  const rOut = 15, rIn = 11; // ring band (chunky)
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const d = Math.hypot(x - cx, y - cy);
      const on = d <= rOut && d >= rIn;
      const i = (y * S + x) * 4;
      img.data[i] = img.data[i + 1] = img.data[i + 2] = 200; // soft white — below the bloom threshold so it doesn't glow
      img.data[i + 3] = on ? 255 : 0;
    }
  }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(c);
  tex.magFilter = THREE.NearestFilter; tex.minFilter = THREE.NearestFilter; tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export class SelectRing {
  private readonly mesh: THREE.Mesh;

  constructor(scene: THREE.Scene) {
    const mat = new THREE.MeshBasicMaterial({
      // depthTest ON so the ring is occluded by anything in front of it (no seeing it through walls/
      // objects); depthWrite OFF so it doesn't z-fight or block other transparent sprites.
      map: makeRingTexture(), transparent: true, depthWrite: false, depthTest: true, fog: false,
    });
    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(DIAM, DIAM), mat);
    this.mesh.rotation.x = -Math.PI / 2; // lay flat on the ground
    this.mesh.renderOrder = 1;
    this.mesh.visible = false;
    this.mesh.raycast = () => {}; // never catch clicks
    scene.add(this.mesh);
  }

  /** Place the ring under (x,z) sitting just above ground height `y`, and show it. The clearance
   *  (0.12) is enough to sit ABOVE the flat ground decals that share this depth band — the Kapon
   *  paved lane (0.05), dirt trail (0.045) and water sheets (0.07) — so they can't paint over the
   *  ring, while depthTest stays ON so real walls/props still occlude it. */
  setAt(x: number, y: number, z: number): void {
    this.mesh.position.set(x, y + 0.12, z);
    this.mesh.visible = true;
  }

  hide(): void { this.mesh.visible = false; }
}
